import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SharePanel from '@/components/feature/SharePanel';
import { analyses, findAnalysis } from '@/mocks/analyses';
import { findCategory } from '@/mocks/categories';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useState } from 'react';

export default function AnalysisPage() {
  const { analysisId = '' } = useParams();
  const analysis = findAnalysis(analysisId);
  const [shareOpen, setShareOpen] = useState(false);
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);

  const category = analysis ? findCategory(analysis.categoryId) : undefined;
  const title = analysis ? analysis.title : '数据分析专题';

  usePageMeta({
    title: `【${title}】数据分析专题 - 51每日大赛`,
    description: analysis
      ? `${analysis.subtitle}。${analysis.intro.slice(0, 60)}...`
      : '51每日大赛数据分析专题，查看黑5数据分组统计与趋势解读。',
    keywords: analysis
      ? `${analysis.title},黑5数据分析,赛事分析,51每日大赛`
      : '数据分析,51每日大赛',
  });

  if (!analysis) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mx-auto">
          <i className="ri-file-chart-line w-7 h-7 flex items-center justify-center text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该专题</h1>
        <p className="mt-2 text-sm text-foreground-600">该数据分析专题可能已被移除。</p>
        <Link
          to="/"
          className="mt-6 inline-flex px-5 py-2.5 rounded-md bg-primary-700 text-background-50 text-sm hover:bg-primary-800 transition-colors whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = analysis.relatedIds
    .map((id) => findAnalysis(id))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
  const others = analyses.filter((item) => item.id !== analysis.id).slice(0, 3);

  return (
    <div className="w-full px-4 md:px-6 pt-6 md:pt-8 pb-10 md:pb-12">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '数据分析', to: '/analysis/an001' },
          { label: analysis.title },
        ]}
      />

      <div className="mt-4 bg-primary-900 rounded-lg overflow-hidden relative">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-accent-500/15" />
        <div className="relative p-5 md:p-8">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-accent-500 text-background-50">
              {category?.shortName ?? '综合'}专题
            </span>
            <span className="text-xs text-primary-300">更新于 {analysis.updatedAt}</span>
            <span className="text-xs text-primary-300">· {analysis.readTime}</span>
            <span className="text-xs text-primary-300">· {analysis.author}</span>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight max-w-4xl">
            {analysis.title}
          </h1>
          <p className="mt-3 text-sm md:text-base text-primary-200 max-w-3xl">{analysis.subtitle}</p>
        </div>
      </div>

      <div className="mt-6 flex flex-col lg:flex-row gap-6 md:gap-8">
        <div className="flex-1 min-w-0 flex flex-col gap-5 md:gap-6">
          <section className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <div className="w-full h-48 md:h-64 bg-background-200">
              <img
                src={analysis.cover}
                alt={analysis.title}
                title={`${analysis.title} 数据分析专题`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-5 md:p-6">
              <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-3">
                专题导语
              </h2>
              <p className="text-sm md:text-[15px] text-foreground-700 leading-relaxed">{analysis.intro}</p>
            </div>
          </section>

          {analysis.groups.map((group, index) => (
            <section
              key={group.name}
              className="bg-background-50 border border-background-200 rounded-lg overflow-hidden"
            >
              <div className="flex items-center justify-between gap-3 px-5 md:px-6 py-4 border-b border-background-200">
                <div>
                  <h2 className="font-heading text-base font-semibold text-foreground-950 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-heading">
                      {index + 1}
                    </span>
                    {group.name}
                  </h2>
                  <p className="mt-1 text-xs text-foreground-500">{group.desc}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setZoomIndex(index)}
                  className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-md text-xs border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-zoom-in-line w-3.5 h-3.5 flex items-center justify-center" />
                  放大查看
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[420px]">
                  <thead>
                    <tr className="bg-background-100 text-xs text-foreground-500">
                      <th className="text-left font-medium px-5 py-3">指标 / 对象</th>
                      <th className="text-right font-medium px-5 py-3">数值</th>
                      <th className="text-right font-medium px-5 py-3">环比变化</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200">
                    {group.rows.map((row) => {
                      const positive = row.change.startsWith('+');
                      const fallbackDash = row.change === '+';
                      return (
                        <tr key={row.label} className="hover:bg-background-100/60 transition-colors">
                          <td className="px-5 py-3.5 text-sm text-foreground-900">{row.label}</td>
                          <td className="px-5 py-3.5 text-right font-heading text-sm font-semibold text-primary-700">
                            {row.value}
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <span
                              className={`inline-flex items-center gap-1 text-xs ${
                                fallbackDash
                                  ? 'text-foreground-500'
                                  : positive
                                    ? 'text-accent-600'
                                    : 'text-secondary-600'
                              }`}
                            >
                              {!fallbackDash ? (
                                <i
                                  className={`${positive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} w-3.5 h-3.5 flex items-center justify-center`}
                                />
                              ) : null}
                              {row.change}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}

          <section className="bg-secondary-50 border border-secondary-200 rounded-lg p-5 md:p-6">
            <h2 className="font-heading text-base md:text-lg font-semibold text-secondary-900 flex items-center gap-2 mb-3">
              <i className="ri-lightbulb-line w-5 h-5 flex items-center justify-center" />
              专题结论
            </h2>
            <p className="text-sm md:text-[15px] text-secondary-900/90 leading-relaxed">
              {analysis.conclusion}
            </p>
          </section>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center" />
              分享专题
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-5-line w-4 h-4 flex items-center justify-center" />
              返回首页
            </Link>
            {category ? (
              <Link
                to={`/category/${category.id}`}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-layout-grid-line w-4 h-4 flex items-center justify-center" />
                进入{category.name}
              </Link>
            ) : null}
          </div>
        </div>

        <aside className="w-full lg:w-72 shrink-0">
          <div className="lg:sticky lg:top-24 flex flex-col gap-4">
            {related.length > 0 ? (
              <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                <h3 className="font-heading text-sm font-semibold text-foreground-950 px-4 py-3 border-b border-background-200">
                  相关推荐专题
                </h3>
                <ul className="divide-y divide-background-200">
                  {related.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/analysis/${item.id}`}
                        className="group block px-4 py-3.5 hover:bg-background-100/70 transition-colors"
                      >
                        <h4 className="text-sm text-foreground-900 leading-snug group-hover:text-primary-700 transition-colors line-clamp-2">
                          {item.title}
                        </h4>
                        <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{item.subtitle}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
              <h3 className="font-heading text-sm font-semibold text-foreground-950 px-4 py-3 border-b border-background-200">
                更多专题
              </h3>
              <ul className="divide-y divide-background-200">
                {others.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/analysis/${item.id}`}
                      className="group block px-4 py-3.5 hover:bg-background-100/70 transition-colors"
                    >
                      <h4 className="text-sm text-foreground-900 leading-snug group-hover:text-primary-700 transition-colors line-clamp-2">
                        {item.title}
                      </h4>
                      <span className="mt-1 inline-block text-[11px] text-foreground-500">
                        {item.readTime}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </div>

      {zoomIndex !== null ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-950/60 animate-fade-in" onClick={() => setZoomIndex(null)} />
          <div className="relative w-full max-w-3xl bg-background-50 rounded-lg p-5 md:p-6 animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                {analysis.groups[zoomIndex].name}
              </h3>
              <button
                type="button"
                onClick={() => setZoomIndex(null)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-colors"
                aria-label="关闭"
              >
                <i className="ri-close-line w-5 h-5 flex items-center justify-center" />
              </button>
            </div>
            <div className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-1">
              {analysis.groups[zoomIndex].rows.map((row) => {
                const numeric = parseFloat(row.value);
                const pct = Number.isNaN(numeric) ? 50 : Math.min(100, Math.max(6, numeric));
                return (
                  <div key={row.label}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-foreground-800">{row.label}</span>
                      <span className="font-heading font-semibold text-primary-700">{row.value}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-background-200 overflow-hidden">
                      <div className="h-full bg-accent-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      <SharePanel title={analysis.title} open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );
}